import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Text } from '@telegram-apps/telegram-ui';
import type { Comment } from '../types';

interface CommentThreadProps {
  comments: Comment[];
  isLoading: boolean;
  error?: string | null;
  onAddComment: (text: string) => Promise<void>;
}

/**
 * Лента комментариев + поле ввода
 */
export function CommentThread({ comments, isLoading, error, onAddComment }: CommentThreadProps) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  // Автоскролл к последнему комментарию
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  async function handleSend() {
    if (!text.trim() || isSending) return;

    setIsSending(true);
    setSendError(null);
    try {
      await onAddComment(text.trim());
      setText('');
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Не удалось отправить комментарий');
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  /**
   * Форматирование даты комментария
   */
  function formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      }

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Заголовок */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--tgui--divider_color, #e0e0e0)' }}>
        <Text weight="2" style={{ fontSize: '15px' }}>
          Комментарии ({comments.length})
        </Text>
      </div>

      {/* Список комментариев */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minHeight: '100px',
          maxHeight: '300px',
        }}
      >
        {isLoading && comments.length === 0 && (
          <Text style={{ textAlign: 'center', color: 'var(--tgui--hint_color)' }}>
            Загрузка...
          </Text>
        )}

        {/* Ошибка загрузки */}
        {error && (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            backgroundColor: 'var(--tgui--secondary_bg_color, #f5f5f5)',
            borderRadius: '8px',
            margin: '8px 0',
          }}>
            <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '13px' }}>
              {error}
            </Text>
          </div>
        )}

        {!isLoading && !error && comments.length === 0 && (
          <Text style={{ textAlign: 'center', color: 'var(--tgui--hint_color)', padding: '20px 0' }}>
            Нет комментариев
          </Text>
        )}

        {comments.map((comment) => (
          <div
            key={comment.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: comment.isClient ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              alignSelf: comment.isClient ? 'flex-end' : 'flex-start',
            }}
          >
            {/* Автор */}
            <span style={{ fontSize: '11px', color: 'var(--tgui--hint_color)', marginBottom: '2px' }}>
              {comment.isClient ? 'Вы' : comment.authorName}
            </span>

            {/* Пузырёк */}
            <div
              style={{
                padding: '8px 12px',
                borderRadius: comment.isClient ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                backgroundColor: comment.isClient
                  ? 'var(--tgui--button_color, #2196f3)'
                  : 'var(--tgui--secondary_bg_color, #f0f0f0)',
                color: comment.isClient ? '#fff' : 'var(--tgui--text_color)',
                fontSize: '14px',
                lineHeight: '1.4',
                wordBreak: 'break-word',
              }}
            >
              {comment.message}
            </div>

            {/* Время */}
            <span style={{ fontSize: '10px', color: 'var(--tgui--hint_color)', marginTop: '2px' }}>
              {formatDate(comment.createdAt)}
            </span>
          </div>
        ))}

        <div ref={listEndRef} />
      </div>

      {/* Ошибка отправки */}
      {sendError && (
        <div style={{ padding: '4px 16px' }}>
          <Text style={{ color: 'var(--tgui--destructive_text_color, #ff3b30)', fontSize: '12px' }}>
            {sendError}
          </Text>
        </div>
      )}

      {/* Поле ввода */}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--tgui--divider_color, #e0e0e0)',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          <Input
            placeholder="Написать комментарий..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <Button
          size="s"
          onClick={handleSend}
          loading={isSending}
          disabled={!text.trim() || isSending}
        >
          Отправить
        </Button>
      </div>
    </div>
  );
}
